import { BsPlusCircle } from 'react-icons/bs';
import { daysOfTheWeekS, notificationSettingTypes, notificationTypes } from '@monorepo/helpers';
import { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer } from 'src/store';
import { notificationServices } from 'src/services';
import { NotificationsSettingsCrudModal } from './NotificationsSettingsCrudModal';
import { typeNotificationsDoc, typeNotificationSetting, typeNotificationType, typeUser } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetIsDarkMode } from 'src/custom-hooks';

type propsType = {
    head1Id: number | null | undefined;
    head1OriginalId: number | null | undefined;
    head2Id: number | null | undefined;
    head2OriginalId: number | null | undefined;
    notificationsDoc: typeNotificationsDoc;
    notificationSettings: typeNotificationSetting[];
    newNotificationSettings: typeNotificationSetting[];
    setNewNotificationSettings: Dispatch<SetStateAction<typeNotificationSetting[]>>;
    setRefreshCounter: Dispatch<SetStateAction<number>>;
    type: typeNotificationType;
    updateHead1Id: (id: number | null | undefined) => void;
    updateHead2Id: (id: number | null | undefined) => void;
    users: typeUser[];
};

export const NotificationsSettings: FC<propsType> = ({
    head1Id, head1OriginalId, head2Id, head2OriginalId, notificationsDoc,
    notificationSettings, newNotificationSettings, setNewNotificationSettings,
    setRefreshCounter, type, updateHead1Id, updateHead2Id, users
}) => {
    const [notificationSettingToEdit, setNotificationSettingToEdit] = useState<typeNotificationSetting | null>(null);
    const [showCrudModal, setShowCrudModal] = useState(false);
    const dispatch = useDispatch();
    const isDarkMode = useGetIsDarkMode();

    const isValid: boolean = useMemo(() =>
        JSON.stringify(newNotificationSettings) !== JSON.stringify(notificationSettings)
        || (head1Id !== head1OriginalId)
        || (head2Id !== head2OriginalId)
    , [head1Id, head1OriginalId, head2Id, head2OriginalId, newNotificationSettings, notificationsDoc, notificationSettings]);

    const saveHandler = () => {
        if (!isValid) {
            return;
        }
        dispatch(setValuesAndOpenAlertModalReducer({
            title: 'Confirmar',
            message: `Se va a guardar el esquema actual de notificaciones para ${notificationTypes.find(nt => nt.type === type)?.label}`,
            mode: 'confirm',
            execution: async () => {
                const newNotificationsDoc = structuredClone(notificationsDoc);
                switch (type) {
                    case 'carts':
                        newNotificationsDoc.carts = newNotificationSettings;
                        newNotificationsDoc.cartHeadOfDepartmentId = head1Id;
                        break;
                    case 'meetings-midweek-assignment':
                        newNotificationsDoc.midweek = newNotificationSettings;
                        newNotificationsDoc.midweekHeadOfDepartmentId = head1Id;
                        break;
                    case 'meetings-midweek-av-ushers':
                        newNotificationsDoc.midweekAVUshers = newNotificationSettings;
                        newNotificationsDoc.avHeadOfDepartmentId = head1Id;
                        newNotificationsDoc.usherHeadOfDepartmentId = head2Id;
                        break;
                    case 'meetings-midweek-cleaning':
                        newNotificationsDoc.midweekCleaning = newNotificationSettings;
                        break;
                    case 'meetings-public-assignment':
                        newNotificationsDoc.public = newNotificationSettings;
                        newNotificationsDoc.publicHeadOfDepartmentId = head1Id;
                        break;
                    case 'meetings-public-av-ushers':
                        newNotificationsDoc.publicAVUshers = newNotificationSettings;
                        newNotificationsDoc.avHeadOfDepartmentId = head1Id;
                        newNotificationsDoc.usherHeadOfDepartmentId = head2Id;
                        break;
                    case 'meetings-public-cleaning':
                        newNotificationsDoc.publicCleaning = newNotificationSettings;
                        break;
                    case 'public-ministry':
                        newNotificationsDoc.publicMinistry = newNotificationSettings;
                        newNotificationsDoc.pmHeadOfDepartmentId = head1Id;
                        break;
                    default:
                        break;
                }
                dispatch(showLoadingModalReducer());
                const response = await notificationServices.SetOptions(newNotificationsDoc);
                dispatch(hideLoadingModalReducer());
                if (!response.success) {
                    dispatch(setAlertResponse({ response }));
                    return;
                }
                setRefreshCounter(x => x + 1);
                dispatch(setValuesAndOpenAlertModalReducer({
                    title: 'Logrado',
                    message: '',
                    animation: 1,
                    mode: 'alert'
                }));
            }
        }));
    }

    const cancelHandler = () => {
        dispatch(setValuesAndOpenAlertModalReducer({
            title: 'Cancelar',
            message: 'Se van a perder los cambios no guardados',
            animation: 3,
            mode: 'confirm',
            execution: () => {
                setRefreshCounter(x => x + 1);
            }
        }));
    }

    return (
        <>
        {(showCrudModal || notificationSettingToEdit) &&
            <NotificationsSettingsCrudModal
                addNotificationSetting={notificationSettingToEdit ?
                    undefined
                    :
                    (ns: typeNotificationSetting) => {
                        setNewNotificationSettings(x => [...x, ns]);
                    }
                }
                closeModal={() => {
                    setNotificationSettingToEdit(null);
                    setShowCrudModal(false);
                }}
                deleteNotificationSetting={notificationSettingToEdit ?
                    (nsId: number) => {
                        setNewNotificationSettings(x => x.filter(ns => ns.id !== nsId));
                    }
                    :
                    undefined
                }
                editNotificationSetting={notificationSettingToEdit ?
                    (ns: typeNotificationSetting) => {
                        setNewNotificationSettings(x => x.map(_ns => _ns.id === ns.id ? ns : _ns));
                    }
                    :
                    undefined
                }
                head1Id={head1Id}
                head2Id={head2Id}
                newNotificationSettings={newNotificationSettings}
                notificationSettingToEdit={notificationSettingToEdit}
                type={type}
                updateHead1Id={updateHead1Id}
                updateHead2Id={updateHead2Id}
                users={users}
            />
        }
        <div className={`${isDarkMode ? 'bg-lightdark' : 'bg-lightgrey'} border rounded p-3`}>
            <div className={'text-center mt-3 mb-4'}>
                {newNotificationSettings.length === 4 ?
                    <>Se permiten hasta 4 notificaciones</>
                    :
                    <button className={'btn btn-general-red'} onClick={() => setShowCrudModal(true)}>
                        <BsPlusCircle className={'mb-1 me-2'} />Agregar Notificación
                    </button>
                }
            </div>
            {!!newNotificationSettings.length ?
                <div className={'table-responsive mt-4'}>
                    <table className={'table table-striped table-bordered border border-2 table-dark mx-auto'}
                        style={{ maxWidth: '800px' }}
                    >
                        <thead>
                            <tr>
                                <th>Esquemas de Notificaciones</th>
                                <th className={'text-center'}>Editar</th>
                                <th colSpan={2}>Responsables de Reemplazos</th>
                            </tr>
                        </thead>
                        <tbody>
                            {newNotificationSettings
                             .sort((ns1, ns2) => (ns1.type - ns2.type) || ((ns1.weekday || 7) - ( ns2.weekday || 7)) || (ns1.hour - ns2.hour))
                             .map((ns, i) =>
                                <tr key={ns.id}>
                                    <td>
                                        {ns.weekday !== null ?
                                            <>
                                            El {daysOfTheWeekS[ns.weekday]} de {notificationSettingTypes.find(t => t.type === ns.type)?.shortLabel} a las {ns.hour.toString().padStart(2, '0')}:00 hs.
                                            </>
                                            :
                                            <>
                                            {notificationSettingTypes.find(t => t.type === ns.type)?.label} a las {ns.hour.toString().padStart(2, '0')}:00 hs.
                                            </>
                                        }
                                    </td>
                                    <td>
                                        <button className={'btn btn-sm btn-general-red w-100'}
                                            onClick={() => setNotificationSettingToEdit(ns)}
                                        >
                                            Editar
                                        </button>
                                    </td>
                                    {!i &&
                                        <>
                                        <td rowSpan={newNotificationSettings.length}>
                                            {users.find(u => u.id === head1Id)?.name} {users.find(u => u.id === head1Id)?.lastName}
                                        </td>
                                        <td rowSpan={newNotificationSettings.length}>
                                            {users.find(u => u.id === head2Id)?.name} {users.find(u => u.id === head2Id)?.lastName}
                                        </td>
                                        </>
                                    }
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                :
                <div className={'fw-bold text-center mb-3'}>
                    No se programaron notificaciones para {notificationTypes.find(nt => nt.type === type)?.label}
                </div>
            }
            {isValid &&
                <div className={'d-flex justify-content-center my-3'}>
                    <div>
                        <button
                            className={'btn btn-general-blue w-100 fw-bolder mt-4'}
                            style={{ height: '50px' }}
                            onClick={saveHandler}
                        >
                            ACEPTAR CAMBIOS
                        </button>
                        <button
                            className={`btn btn-general-red w-100 fw-bolder mt-2`}
                            style={{ height: '40px' }}
                            onClick={cancelHandler}
                        >
                            CANCELAR
                        </button>
                    </div>
                </div>
            }
        </div>
        </>
    );
}
